import React, { useState, useEffect, useRef } from 'react';

const VersionSelector = ({ versions, selectedVersionNumber, onShow, onRestore, onCompare, restoreDisabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('single');                // 'single' | 'compare'
  const [selectedForCompare, setSelectedForCompare] = useState([]); // up to 2 version numbers
  const containerRef = useRef(null);

  const resetCompareState = () => {
    setMode('single');
    setSelectedForCompare([]);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        resetCompareState();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on versions refresh (template reload)
  useEffect(() => {
    setIsOpen(false);
    resetCompareState();
  }, [versions]);

  // Don't render anything if there are no versions
  if (!versions || versions.length === 0) {
    return null;
  }

  const latestVersion = versions[0];
  const canCompare = versions.length >= 2 && typeof onCompare === 'function';

  // Checkbox helpers
  const isChecked = (versionNum) => selectedForCompare.includes(versionNum);
  const toggleSelectForCompare = (versionNum) => {
    setSelectedForCompare(prev => {
      if (prev.includes(versionNum)) {
        return prev.filter(v => v !== versionNum);
      }
      if (prev.length >= 2) return prev;
      return [...prev, versionNum];
    });
  };

  const handleCompareClick = () => {
    if (selectedForCompare.length !== 2) return;
    const [a, b] = selectedForCompare;
    const versionA = Math.min(a, b);
    const versionB = Math.max(a, b);
    onCompare(versionA, versionB);
    setIsOpen(false);
    resetCompareState();
  };

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setSelectedForCompare([]);
  };

  return (
    <div ref={containerRef} className="d-inline-block position-relative ms-2">
      <button
        type="button"
        className="formField4 d-flex align-items-center gap-2 text-start p-1 feedback-field ev-reg-p-blue ev-radius-xsmall px-2 ms-1"
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (!next) resetCompareState();
        }}
      >
        <span className="ev-bold-reg">
          {selectedVersionNumber === 0
            ? 'Mapped Response'
            : `Agent Version ${selectedVersionNumber || latestVersion.version}`}
        </span>
        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
      </button>

      {isOpen && (
        <div className="ev-card-4 position-absolute top-100 mt-2 p-2 z-1000" style={{ minWidth: '380px' }}>

          {/* ── Mode toggle: Single | Compare ── */}
          {canCompare && (
            <div className="d-flex justify-content-center mb-2">
              <div className="btn-group custom-btn-group-outer" role="group">
                <button
                  type="button"
                  className={`btn genbi-custom-btn-group-inner ${mode === 'single' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                  onClick={() => switchMode('single')}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`btn genbi-custom-btn-group-inner ${mode === 'compare' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                  onClick={() => switchMode('compare')}
                >
                  Compare
                </button>
              </div>
            </div>
          )}

          <div className="scrollable-element vh-20 p-1">
            {versions.map(v => {
              const isMappedVersion = v.version === 0;
              const versionName = isMappedVersion
                ? 'Mapped Response'
                : `Agent Version ${v.version}`;
              const checked = isChecked(v.version);
              const disableCheckbox =
                mode === 'compare' && !checked && selectedForCompare.length >= 2;

              return (
                <div
                  key={v.version}
                  className={`ev-card-7 d-flex justify-content-between align-items-center mb-2 p-2 ev-radius-xsmall${isMappedVersion ? ' mapped-version-card' : ''}`}
                >
                  <div
                    className={`d-flex align-items-center`}
                    onClick={mode === 'compare' && !disableCheckbox ? () => toggleSelectForCompare(v.version) : undefined}
                  >
                    {mode === 'compare' && (
                      <input
                        type="checkbox"
                        className="form-check-input version-compare-checkbox me-2"
                        checked={checked}
                        disabled={disableCheckbox}
                        onChange={() => toggleSelectForCompare(v.version)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${versionName} for comparison`}
                      />
                    )}
                    <span className="ev-small-p-dark me-2">{versionName}</span>
                    {isMappedVersion && (
                      <span className="genbi-source-mapped">
                        <i className="bi bi-file-earmark-text me-1"></i>Original
                      </span>
                    )}
                    {/* {v.source && (
                      <span className={`ev-btn-xsmall ${v.source === 'manual' ? 'genbi-source-manual' : 'genbi-source-ai'}`}>
                        {v.source === 'manual' ? (
                          <><i className="bi bi-person-fill me-1 ev-bold-reg"></i>Manual</>
                        ) : (
                          <><i className="bi bi-stars ev-bold-reg"></i>AI</>
                        )}
                      </span>
                    )} */}
                  </div>

                  {/* Action buttons — only in single mode. Compare mode uses checkbox + footer. */}
                  {mode === 'single' && (
                    <div>
                      <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg avatar-tooltip"
                        disabled={restoreDisabled}
                        data-tooltip={restoreDisabled ? 'Wait for agents to complete' : undefined}
                        onClick={() => {
                          onRestore(v.version, versionName);
                          setIsOpen(false);
                          resetCompareState();
                        }}
                      >
                        Restore Version
                      </button>
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg ms-1"
                        onClick={() => {
                          onShow(v.version, versionName);
                          setIsOpen(false);
                          resetCompareState();
                        }}
                      >
                        Show
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Compare footer (only in compare mode) ── */}
          {mode === 'compare' && (
            <div className="version-compare-footer d-flex justify-content-end align-items-center pt-2 mt-1 border-top">
              <span className="ev-xsmall-p-dark me-2" style={{ opacity: 0.7 }}>
                {selectedForCompare.length}/2 selected
              </span>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg"
                onClick={handleCompareClick}
                disabled={selectedForCompare.length !== 2}
              >
                <i className="bi bi-files me-1"></i>
                Compare
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VersionSelector;